import { A } from '@adornis/base/env-info.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { mongoExistsAndNotNull } from '@chancenwerk/db/utils/mongo.js';
import {
  Mitarbeiter,
  WorkshopsAndTrainingInformation,
} from '@chancenwerk/master-data-management/users/entities/Mitarbeiter.js';
import { Workshop } from '@chancenwerk/workshops/entities/Workshop.js';
import { WorkshopAttendance } from '@chancenwerk/workshops/entities/WorkshopAttendance.js';
import { WorkshopAttendanceStatus } from '@chancenwerk/workshops/entities/WorkshopAttendanceStatus.js';
import { WorkshopTrainingAttendance } from '@chancenwerk/workshops/entities/WorkshopTrainingAttendance.js';
import type { AnyBulkWriteOperation, Filter } from 'mongodb';

export const mutationWorkshopMigration = registerMutation({
  type: () => Boolean,
  operationName: 'mutationWorkshopMigration',
  resolve: (workshopID: string, type: string) => {
    return async () => {
      const workshop = await Workshop.getByID<Workshop>(workshopID)({ _id: 1, requiresTraining: 1 });
      if (!workshop) {
        throw new Error(`Workshop not found: ${workshopID}`);
      }

      const def = WorkshopsAndTrainingInformation.workshopMigration[type];
      if (!def) {
        throw new Error(`Unknown migration type: ${type}`);
      }
      const hasTraining = !!def.carriedOut;

      if ((workshop.requiresTraining && !hasTraining) || (!workshop.requiresTraining && hasTraining)) {
        throw new Error('Ziel-Workshop ist nicht kompatibel');
      }

      const workshopEventCol = await getRawCollection<PartialEntityData<WorkshopAttendance>>(
        WorkshopAttendance._collectionName,
      );
      const userCol = await getRawCollection<Mitarbeiter>(Mitarbeiter._collectionName);
      const search: Filter<Mitarbeiter> = {
        $or: [],
      };

      if (hasTraining) {
        search.$or?.push(mongoExistsAndNotNull(`workshopsAndTrainingInformation.${def.training}`));
        search.$or?.push(mongoExistsAndNotNull(`workshopsAndTrainingInformation.${def.carriedOut}`));
      } else {
        search.$or?.push(mongoExistsAndNotNull(`workshopsAndTrainingInformation.${def.training}`));
      }

      // * NASA concept: always define a upper bound instead of a while true
      for (let i = 0; i < 1000; i++) {
        const users = await userCol
          .find(search, {
            projection: {
              _id: 1,
              [`workshopsAndTrainingInformation.${def.training}`]: 1,
              [`workshopsAndTrainingInformation.${def.carriedOut}`]: 1,
            },
          })
          .limit(100)
          .skip(i * 100)
          .toArray();

        const writes: Array<AnyBulkWriteOperation<PartialEntityData<WorkshopAttendance>>> = [];
        for (const user of users) {
          if (hasTraining) {
            const training = user.workshopsAndTrainingInformation?.[def.training];
            if (training) {
              writes.push({
                insertOne: {
                  document: {
                    _id: A.getGloballyUniqueID(),
                    _class: WorkshopTrainingAttendance._class,
                    userID: user._id,
                    workshopID,
                    state: WorkshopAttendanceStatus.ATTENDED,
                    date: user.workshopsAndTrainingInformation?.[def.training],
                  },
                },
              });
            }

            const carriedOut = def.carriedOut ? user.workshopsAndTrainingInformation?.[def.carriedOut] : null;
            if (carriedOut) {
              writes.push({
                insertOne: {
                  document: {
                    _id: A.getGloballyUniqueID(),
                    _class: WorkshopAttendance._class,
                    userID: user._id,
                    workshopID,
                    attendanceCount: 0,
                    state: WorkshopAttendanceStatus.ATTENDED,
                    date: user.workshopsAndTrainingInformation?.[def.carriedOut],
                  },
                },
              });
            }
          } else {
            writes.push({
              insertOne: {
                document: {
                  _id: A.getGloballyUniqueID(),
                  _class: WorkshopAttendance._class,
                  userID: user._id,
                  workshopID,
                  state: WorkshopAttendanceStatus.ATTENDED,
                  date: user.workshopsAndTrainingInformation?.[def.training],
                },
              },
            });
          }
        }

        await workshopEventCol.bulkWrite(writes);

        if (users.length < 100) {
          break;
        }
      }

      return true;
    };
  },
  options: {},
  params: [
    { type: () => String, name: 'workshopID' },
    { type: () => String, name: 'type' },
  ],
});
